/**
 * Organization Repository
 * 사업자 조직 데이터 액세스 계층
 */

import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import {
  Organization,
  OrganizationApprovalRequest,
  OrganizationRejectionRequest,
  OrganizationUpdateRequest,
  ServiceType,
} from '../models';

export class OrganizationRepository {
  // Step 1: 기존 작동하는 API 사용 (/v1/admin/owner-approvals)
  private baseUrl = `${API_BASE_URL}/api/v1/admin/owner-approvals`;

  private getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async getAllOrganizations(serviceType?: ServiceType): Promise<Organization[]> {
    // TODO: 추후 /api/admin/organizations 로 마이그레이션
    const response = await axios.get(`${this.baseUrl}/all`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getPendingOrganizations(serviceType?: ServiceType): Promise<Organization[]> {
    const response = await axios.get(`${this.baseUrl}/pending`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getApprovedOrganizations(serviceType?: ServiceType): Promise<Organization[]> {
    const response = await axios.get(`${this.baseUrl}/approved`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getRejectedOrganizations(serviceType?: ServiceType): Promise<Organization[]> {
    const response = await axios.get(`${this.baseUrl}/rejected`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getOrganizationById(id: string): Promise<Organization> {
    const response = await axios.get(`${this.baseUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async approveOrganization(request: OrganizationApprovalRequest): Promise<Organization> {
    const response = await axios.post(
      `${this.baseUrl}/${request.organization_id}/approve`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data.organization;
  }

  async rejectOrganization(request: OrganizationRejectionRequest): Promise<Organization> {
    const response = await axios.post(
      `${this.baseUrl}/${request.organization_id}/reject`,
      { rejection_reason: request.rejection_reason },
      { headers: this.getAuthHeaders() }
    );
    return response.data.organization;
  }

  async getPendingCount(): Promise<number> {
    try {
      // TODO: 백엔드에 /stats 엔드포인트 구현 후 이 로직으로 변경:
      // const response = await axios.get(`${this.baseUrl}/stats`, ...)
      // return response.data.pending_count || 0;

      // 임시: pending 목록을 가져와서 개수 계산
      const response = await axios.get(`${this.baseUrl}/pending`, {
        headers: this.getAuthHeaders(),
      });
      return Array.isArray(response.data) ? response.data.length : 0;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.warn(`Failed to get pending count (${error.response?.status}):`, error.message);
      } else {
        console.warn('Failed to get pending count:', error);
      }
      return 0;
    }
  }

  /**
   * 사용 중지된 사업자 목록 조회
   */
  async getSuspendedOrganizations(): Promise<Organization[]> {
    const response = await axios.get(`${this.baseUrl}/suspended`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * 사업자 사용 중지
   */
  async suspendOrganization(organizationId: string, reason: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(
      `${this.baseUrl}/${organizationId}/suspend`,
      { reason },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * 사업자 재활성화
   */
  async reactivateOrganization(organizationId: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(
      `${this.baseUrl}/${organizationId}/reactivate`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}
